import { StatsigClient } from "@statsig/js-client";
import { ConfigResponse } from "@zoom/appssdk";

import { getSessionStorage } from "@app/common/storage";
import { Client, MeetingPlatform } from "@app/common/types";
import logger from "@app/utils/logger";
import trackerMixpanel from "@app/utils/tracking/TrackerMixpanel";
import { isBotTraffic } from "@app/utils/utils";

import { TrackerProvider, TrackerUser } from "./types";

let trackerGoogleAnalytics: TrackerProvider | null = null;
if (
  !import.meta.env.VITE_APP_DISABLE_GA ||
  import.meta.env.VITE_APP_DISABLE_GA === "false"
) {
  import("@app/utils/tracking/TrackerGoogleAnalytics.js").then((module) => {
    trackerGoogleAnalytics = module.default;
  });
}

interface InitProps {
  gaId?: string;
  mixpanelId: string;
  debug?: boolean;
  cookieDomain?: string;
  apiHost?: string;
}

type PageView = {
  page_title?: string;
  page_location?: string;
  page_path?: string;
};

// initialize the tracker by default unless the user is impersonating
const getInitialTrackerEnabled = () => {
  try {
    const isImpersonating =
      getSessionStorage().getItem("impersonating") === "true";
    return !isImpersonating;
  } catch (e) {
    return true;
  }
};

interface EventOptions {
  statsig?: true | { eventNameOverride?: string; valueKey?: string };
}

class Tracker implements Omit<TrackerProvider, "initialize"> {
  private trackers: TrackerProvider[] = [];
  private enabled: boolean = getInitialTrackerEnabled();

  constructor() {}

  initialize({
    gaId,
    mixpanelId,
    debug = false,
    apiHost,
    cookieDomain,
  }: InitProps) {
    if (this.trackers.length) {
      throw new Error("Tracker already initialized");
    }

    if (
      trackerGoogleAnalytics &&
      gaId &&
      !isBotTraffic(window.location.pathname)
    ) {
      trackerGoogleAnalytics.initialize({ propertyId: gaId, debug });
      this.trackers.push(trackerGoogleAnalytics);
    } else {
      logger.info("GA not initialized");
    }

    trackerMixpanel.initialize({
      propertyId: mixpanelId,
      debug,
      apiHost,
      cookieDomain,
    });
    this.trackers.push(trackerMixpanel);
  }

  enable() {
    this.enabled = true;
  }

  disable() {
    this.enabled = false;
  }

  event(action: string, params?: Record<string, any>, options?: EventOptions) {
    if (!this.enabled) return;

    if (options?.statsig) {
      const { eventNameOverride, valueKey } =
        options.statsig === true
          ? { eventNameOverride: null, valueKey: null }
          : options.statsig;

      // transform params into Record<string, string> to avoid type errors
      const eventName = eventNameOverride ?? action;
      const metadata = Object.entries(params || {}).reduce(
        (acc, [key, value]) => {
          acc[key] = value.toString();
          return acc;
        },
        {} as Record<string, string>
      );

      const value = params?.[valueKey ?? "value"] ?? null;

      logger.debug("statsig event", { eventName, value, metadata });
      StatsigClient.instance().logEvent(eventName, value, metadata);
    }

    this.trackers.forEach((tracker) => tracker.event(action, params));
  }

  pageviewEvent(pageview: PageView) {
    if (!this.enabled) return;
    this.trackers.forEach((tracker) => tracker.pageviewEvent(pageview));
  }

  setMeetingPlatform(meetingPlatform: MeetingPlatform) {
    if (!this.enabled) return;
    this.trackers.forEach((tracker) =>
      tracker.setMeetingPlatform(meetingPlatform)
    );
  }

  setClient(client: Client) {
    if (!this.enabled) return;
    this.trackers.forEach((tracker) => tracker.setClient(client));
  }

  setUser(
    user: TrackerUser | null,
    currentUser?: TrackerUser | null,
    client?: Client | null
  ) {
    if (!this.enabled) return;
    this.trackers.forEach((tracker) =>
      tracker.setUser(user, currentUser, client)
    );
  }

  setRunningContext(runningContext: string) {
    if (!this.enabled) return;
    this.trackers.forEach((tracker) =>
      tracker.setRunningContext(runningContext)
    );
  }

  setMeetingRole(meetingRole: string) {
    if (!this.enabled) return;
    this.trackers.forEach((tracker) => tracker.setMeetingRole(meetingRole));
  }

  setPeopleProperties(properties: object) {
    if (!this.enabled) return;
    this.trackers.forEach((tracker) => tracker.setPeopleProperties(properties));
  }

  setZoomConfig(zoomConfig: ConfigResponse) {
    if (!this.enabled) return;
    this.trackers.forEach((tracker) => tracker.setZoomConfig(zoomConfig));
  }

  resetUser() {
    if (!this.enabled) return;
    this.trackers.forEach((tracker) => tracker.resetUser());
  }

  register(params: object) {
    if (!this.enabled) return;
    this.trackers.forEach((tracker) => tracker.register(params));
  }
}

const tracker = new Tracker();

const sendEvent =
  <Type extends object>(eventName: string, options?: EventOptions) =>
  (params?: Type) =>
    tracker.event(eventName, params, options);

export { sendEvent };
export default tracker;
